import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Newspaper,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Filter,
} from "lucide-react";
import { Label } from "@/components/ui/label";

// Environmental news is served from Supabase Edge Functions
const NEWS_FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/news`;

interface NewsArticle {
  title: string;
  link: string;
  published: string;
  source: string;
  summary: string;
  category: string;
  image_url?: string;
  author?: string;
  tags: string[];
}

interface NewsSource {
  name: string;
  category: string;
  url: string;
}

interface NewsResponse {
  articles: NewsArticle[];
  total: number;
  category: string;
  timestamp: string;
  mock: boolean;
}

interface SourcesResponse {
  sources: NewsSource[];
  mock: boolean;
}

// Fallback news data in case API call fails
const FALLBACK_NEWS: NewsArticle[] = [
  {
    title: "Global Renewable Energy Capacity Hits Record Growth",
    source: "Mock Environmental News",
    published: "2025-11-15T12:00:00Z",
    summary:
      "Global renewable energy capacity achieved unprecedented growth, marking a significant milestone in the transition to clean energy.",
    link: "https://www.iea.org/reports/renewables-2023",
    category: "sustainability",
    tags: ["Renewable Energy", "Climate Action", "Sustainability"],
    image_url:
      "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?q=80&w=200&auto=format",
  },
  {
    title: "New Marine Protected Area Established in the Pacific",
    source: "Mock Conservation News",
    published: "2025-11-10T12:00:00Z",
    summary:
      "A new marine sanctuary covering over 100,000 square kilometers has been established to protect endangered marine species.",
    link: "https://www.unep.org/news-and-stories",
    category: "nature",
    tags: ["Ocean Conservation", "Marine Life", "Protected Areas"],
    image_url:
      "https://images.unsplash.com/photo-1582967788606-a171c1080cb0?q=80&w=200&auto=format",
  },
  {
    title: "Climate Summit Concludes with New Emissions Targets",
    source: "Mock Climate News",
    published: "2025-11-05T12:00:00Z",
    summary:
      "World leaders agree on ambitious new targets to reduce carbon emissions by 2030.",
    link: "https://www.unfccc.int",
    category: "climate",
    tags: ["Climate Change", "Policy", "Emissions"],
    image_url:
      "https://images.unsplash.com/photo-1561633835-597971ca8a9e?q=80&w=200&auto=format",
  },
];

interface EnvNewsPanelProps {
  className?: string;
}

const EnvNewsPanel: React.FC<EnvNewsPanelProps> = ({ className }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [filterMode, setFilterMode] = useState<"category" | "source">(
    "category",
  );
  const [newsItems, setNewsItems] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Environmental News API client functions
  const fetchNews = async (
    limit: number = 15,
    category?: string,
  ): Promise<NewsResponse> => {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    if (category && category !== "all") {
      params.append("category", category);
    }

    const response = await fetch(`${NEWS_FUNCTIONS_URL}?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch news: ${response.statusText}`);
    }

    return response.json();
  };

  const fetchCategories = async (): Promise<string[]> => {
    try {
      const response = await fetch(`${NEWS_FUNCTIONS_URL}/categories`);
      if (!response.ok) {
        // Return fallback categories instead of throwing error
        console.log(
          `Categories endpoint unavailable (${response.status}), using fallback data`,
        );
        return ["environmental", "climate", "sustainability", "nature"];
      }

      const data = await response.json();
      return data.categories;
    } catch (error) {
      console.log("Categories API unavailable, using fallback data:", error);
      return ["environmental", "climate", "sustainability", "nature"];
    }
  };

  const fetchSources = async (): Promise<NewsSource[]> => {
    try {
      const response = await fetch(`${NEWS_FUNCTIONS_URL}/sources`);
      if (!response.ok) {
        // Return fallback sources instead of throwing error
        console.log(
          `Sources endpoint unavailable (${response.status}), using fallback data`,
        );
        return [
          { name: "BBC Environment", category: "environmental", url: "" },
          { name: "Climate Central", category: "climate", url: "" },
          {
            name: "Environmental News Network",
            category: "sustainability",
            url: "",
          },
          { name: "Nature Conservation", category: "nature", url: "" },
        ];
      }

      const data = await response.json();
      return data.sources;
    } catch (error) {
      console.log("Sources API unavailable, using fallback data:", error);
      return [
        { name: "BBC Environment", category: "environmental", url: "" },
        { name: "Climate Central", category: "climate", url: "" },
        {
          name: "Environmental News Network",
          category: "sustainability",
          url: "",
        },
        { name: "Nature Conservation", category: "nature", url: "" },
      ];
    }
  };

  const fetchNewsBySources = async (
    sources: string[],
    limit: number = 15,
  ): Promise<NewsArticle[]> => {
    if (sources.length === 0) return [];

    const allArticles: NewsArticle[] = [];

    for (const source of sources) {
      try {
        const response = await fetch(
          `${NEWS_FUNCTIONS_URL}/source/${encodeURIComponent(source)}?limit=${Math.ceil(limit / sources.length)}`,
        );
        if (response.ok) {
          const data = await response.json();
          allArticles.push(...data.articles);
        }
      } catch (error) {
        console.error(`Error fetching from ${source}:`, error);
      }
    }

    // Sort all articles by published date
    allArticles.sort(
      (a, b) =>
        new Date(b.published).getTime() - new Date(a.published).getTime(),
    );

    return allArticles.slice(0, limit);
  };

  // Function to load news
  const loadNews = async () => {
    setIsLoading(true);
    setError(null);
    setUsingFallback(false);

    try {
      let articles: NewsArticle[] = [];

      if (filterMode === "source" && selectedSources.length > 0) {
        // Fetch news from selected sources
        articles = await fetchNewsBySources(selectedSources, 15);
      } else {
        // Fetch news by category (default behavior)
        const newsData = await fetchNews(15, selectedCategory);
        articles = newsData.articles;
        setUsingFallback(newsData.mock);
      }

      setNewsItems(articles);

      if (articles.length === 0) {
        throw new Error("No articles found");
      }
    } catch (error) {
      console.error("Error fetching news:", error);
      setError("Could not load news articles. Showing fallback articles.");
      setNewsItems(FALLBACK_NEWS);
      setUsingFallback(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to load categories and sources
  const loadCategoriesAndSources = async () => {
    try {
      const [categoriesData, sourcesData] = await Promise.all([
        fetchCategories(),
        fetchSources(),
      ]);
      setCategories(["all", ...categoriesData]);
      setSources(sourcesData);
      console.log("📰 News categories and sources loaded successfully");
    } catch (error) {
      // This should rarely happen now since individual functions handle their own errors
      console.log(
        "Unexpected error loading news metadata, using complete fallback:",
        error,
      );
      setCategories([
        "all",
        "environmental",
        "climate",
        "sustainability",
        "nature",
      ]);
      setSources([
        { name: "BBC Environment", category: "environmental", url: "" },
        { name: "Climate Central", category: "climate", url: "" },
        {
          name: "Environmental News Network",
          category: "sustainability",
          url: "",
        },
        { name: "Nature Conservation", category: "nature", url: "" },
      ]);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadCategoriesAndSources();
  }, []);

  // Load news when filters change
  useEffect(() => {
    loadNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedSources, filterMode]);

  // Function to handle category selection
  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
  };

  // Function to handle source selection
  const handleSourceToggle = (sourceName: string) => {
    setSelectedSources((prev) => {
      if (prev.includes(sourceName)) {
        return prev.filter((s) => s !== sourceName);
      } else {
        return [...prev, sourceName];
      }
    });
  };

  // Function to handle filter mode change
  const handleFilterModeChange = (mode: "category" | "source") => {
    setFilterMode(mode);
    if (mode === "category") {
      setSelectedSources([]);
    } else {
      setSelectedCategory("all");
    }
  };

  // Function to safely extract string from tag (handles both strings and objects)
  const getTagString = (tag: any): string => {
    if (typeof tag === "string") {
      return tag;
    } else if (typeof tag === "object" && tag !== null) {
      return tag.term || tag.label || tag.name || String(tag);
    }
    return String(tag);
  };

  // Format category name for display
  const formatCategoryName = (category: string): string => {
    if (category === "all") return "All Categories";
    return category
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Card
      className={`flex max-h-[600px] min-h-0 flex-col overflow-hidden ${className} dark:bg-[#272727]`}
    >
      <CardHeader className="shrink-0 pb-3">
        <div className="flex justify-between text-left">
          <CardTitle className="text-xl flex text-left gap-2">
            <Newspaper className="h-5 w-5" />
            Environmental News
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={loadNews}
            disabled={isLoading}
            title="Refresh News"
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
        <div className="mb-[2em]"></div>
        <CardDescription className="text-left">
          Latest environmental updates from RSS feeds planetwide
        </CardDescription>
        <div className="mb-4"></div>

        {/* Filter Mode Selection */}
        <div className="mb-4">
          <Tabs
            value={filterMode}
            onValueChange={(value: string) =>
              handleFilterModeChange(value as "category" | "source")
            }
          >
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="category" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                By Category
              </TabsTrigger>
              <TabsTrigger value="source" className="flex items-center gap-2">
                <Newspaper className="h-4 w-4" />
                By Source
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Category Selection */}
        {filterMode === "category" && (
          <div className="mb-4">
            <Select
              value={selectedCategory}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select news category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${selectedCategory === category ? "bg-[#40e0d0]" : "bg-gray-300"}`}
                      />
                      {formatCategoryName(category)}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Selected category badge */}
            <div className="flex flex-wrap gap-1 mt-4">
              <Badge variant="outline" className="bg-background/50">
                {formatCategoryName(selectedCategory)}
              </Badge>
            </div>
          </div>
        )}

        {/* Source Selection */}
        {filterMode === "source" && (
          <div className="mb-4">
            <Label className="text-sm font-medium mb-3 block">
              Select News Sources:
            </Label>
            <div className="space-y-3 max-h-40 overflow-y-auto border rounded-lg p-3 bg-background/50">
              {sources.map((source) => (
                <div key={source.name} className="flex items-center space-x-2">
                  <Checkbox
                    id={`source-${source.name}`}
                    checked={selectedSources.includes(source.name)}
                    onCheckedChange={() => handleSourceToggle(source.name)}
                  />
                  <Label
                    htmlFor={`source-${source.name}`}
                    className="text-sm font-normal cursor-pointer flex-1 flex items-center justify-between"
                  >
                    <span>{source.name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {source.category}
                    </Badge>
                  </Label>
                </div>
              ))}
            </div>

            {/* Selected sources summary */}
            <div className="flex flex-wrap gap-1 mt-4">
              {selectedSources.length > 0 ? (
                selectedSources.map((source) => (
                  <Badge
                    key={source}
                    variant="outline"
                    className="bg-background/50"
                  >
                    {source}
                  </Badge>
                ))
              ) : (
                <Badge variant="secondary" className="text-muted-foreground">
                  No sources selected
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Status badges */}
        <div className="flex flex-wrap gap-1 mb-4">
          {usingFallback && (
            <Badge
              variant="secondary"
              className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
            >
              Using cached data
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="min-h-0 flex-1 overflow-y-auto pb-8">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-pulse text-muted-foreground">
              Loading environmental news...
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md border border-amber-400/40 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
                <AlertTriangle className="h-5 w-5" />
                <div className="flex-1 text-left text-sm">{error}</div>
                <Button onClick={loadNews} variant="outline" size="sm">
                  Try Again
                </Button>
              </div>
            )}
            {newsItems.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-muted-foreground">
                  No news articles found for this category.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {newsItems.map((news, index) => (
                  <div
                    key={`${news.link}-${index}`}
                    className="p-3 border rounded-lg border-border/40 hover:bg-muted/50 transition-colors dark:border-[#404040]"
                  >
                    <div className="flex gap-3">
                      {news.image_url && (
                        <div className="flex-shrink-0">
                          <img
                            src={news.image_url}
                            alt={news.title}
                            className="w-20 h-20 object-cover rounded-md"
                            onError={(e) => {
                              // Hide image on error
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-lg mb-1 line-clamp-2 text-left">
                          {news.title}
                        </h3>
                        <div className="mt-2 mb-2 text-xs text-muted-foreground text-left">
                          {new Date(news.published).toLocaleDateString()} •{" "}
                          {news.source}
                          {usingFallback && (
                            <span className="ml-2 inline-block text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                              cached
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2 text-left">
                          {news.summary}
                        </p>

                        {/* Author if available */}
                        {news.author && (
                          <div className="text-xs text-muted-foreground mb-2 text-left">
                            By: {news.author}
                          </div>
                        )}

                        {/* Tags and link */}
                        <div className="flex flex-wrap gap-2 justify-start mt-4">
                          {news.tags.slice(0, 3).map((tag, tagIndex) => {
                            const tagString = getTagString(tag);
                            return (
                              <Badge
                                key={`${news.link}-${tagIndex}-${tagString}`}
                                variant="secondary"
                              >
                                {tagString}
                              </Badge>
                            );
                          })}
                          {news.link && news.link.startsWith("http") && (
                            <a
                              href={news.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-[#40e0d0] hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              View article
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EnvNewsPanel;
